import React from "react";

import { useTranslation } from "react-i18next";
import {
  HiCheckCircle,
  HiDocumentText,
  HiExclamationCircle,
  HiLink,
  HiRefresh,
} from "react-icons/hi";

import {
  EmailDomainDnsRecord,
  useCreateEmailDomain,
  useDeleteEmailDomain,
  useEmailDomainStatus,
  useVerifyEmailDomain,
} from "../../../../hooks/entity";
import { UnlockBadge } from "../../../atoms/UnlockBadge";
import { CustomButton } from "../../../atoms/CustomButton";
import { Copy } from "../../../atoms/Copy";
import { ConfigStatusItem } from "../../../atoms/ConfigStatusItem";
import { isReservedSenderDomain } from "../../../../utils/emailSender";
import { FieldLabel } from "../../../atoms/FieldLabel";

type EmailDomainCardProps = {
  customer_identification: string;
  entity_identification: string;
  senderEmail: string;
  isEnterprise: boolean;
  canWriteEntitySettings: boolean;
  onLockedClick: () => void;
};

const senderDomainOf = (email: string): string => {
  const at = (email || "").lastIndexOf("@");
  return at === -1
    ? ""
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
};

export const EmailDomainCard: React.FC<EmailDomainCardProps> = ({
  customer_identification,
  entity_identification,
  senderEmail,
  isEnterprise,
  canWriteEntitySettings,
  onLockedClick,
}) => {
  const { t } = useTranslation();

  const { data: domainStatus, isLoading } = useEmailDomainStatus(
    customer_identification,
    entity_identification,
    { enabled: isEnterprise }
  );
  const { mutate: createDomain, isLoading: isCreating } =
    useCreateEmailDomain();
  const { mutate: verifyDomain, isLoading: isVerifying } =
    useVerifyEmailDomain();
  const { mutate: deleteDomain, isLoading: isDeleting } =
    useDeleteEmailDomain();

  const derivedDomain = senderDomainOf(senderEmail);
  const canDeriveDomain =
    Boolean(derivedDomain) && !isReservedSenderDomain(senderEmail);
  const hasDomain = Boolean(domainStatus?.domain);
  const featureAvailable = domainStatus?.enabled !== false;

  const handleCreate = () => {
    if (!canDeriveDomain || !derivedDomain) return;
    createDomain({
      customer_identification,
      entity_identification,
      domain: derivedDomain,
    });
  };

  const handleVerify = () => {
    verifyDomain({ customer_identification, entity_identification });
  };

  const handleDelete = () => {
    if (
      !window.confirm(
        t("dashboard.entities.emailDomain.removeConfirm") as string
      )
    ) {
      return;
    }
    deleteDomain({ customer_identification, entity_identification });
  };

  const statusBadge = () => {
    if (domainStatus?.status === "verified") {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
          <HiCheckCircle className="h-4 w-4" />
          {t("dashboard.entities.emailDomain.statusVerified")}
        </span>
      );
    }
    if (domainStatus?.status === "failed") {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
          <HiExclamationCircle className="h-4 w-4" />
          {t("dashboard.entities.emailDomain.statusFailed")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
        <HiRefresh className="h-4 w-4" />
        {t("dashboard.entities.emailDomain.statusPending")}
      </span>
    );
  };

  const isDomainVerified = domainStatus?.status === "verified";

  const recordItem = (record: EmailDomainDnsRecord) => (
    <ConfigStatusItem
      key={record.key}
      icon={record.type === "CNAME" ? HiLink : HiDocumentText}
      label={record.type}
      completed={isDomainVerified || record.status}
      pendingVariant="syncing"
    >
      <div className="mt-1 space-y-1 min-w-0">
        {record.host_name && (
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs text-gray-500 shrink-0">
              {t("dashboard.entities.emailDomain.recordHost")}
            </span>
            <div className="font-mono text-xs text-gray-700 min-w-0">
              <Copy target={record.host_name} truncate longVersion />
            </div>
          </div>
        )}
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-xs text-gray-500 shrink-0">
            {t("dashboard.entities.emailDomain.recordValue")}
          </span>
          <div className="font-mono text-xs text-gray-700 min-w-0">
            <Copy target={record.value} truncate longVersion />
          </div>
        </div>
      </div>
    </ConfigStatusItem>
  );

  return (
    <div className="mb-3 w-full">
      <div
        onClick={() => {
          if (!isEnterprise) onLockedClick();
        }}
        className={!isEnterprise ? "cursor-pointer" : ""}
      >
        <div className="flex items-center gap-2 mb-2">
          <FieldLabel className="text-sm font-medium text-gray-700 whitespace-nowrap">
            {t("dashboard.entities.emailDomain.label")}
          </FieldLabel>
          {!isEnterprise && <UnlockBadge />}
          {isEnterprise && hasDomain && statusBadge()}
        </div>

        {!isEnterprise && (
          <p className="text-sm text-gray-500 font-figtree">
            {t("dashboard.entities.emailDomain.lockedHint")}
          </p>
        )}

        {isEnterprise && !featureAvailable && (
          <p className="text-sm text-gray-500 font-figtree">
            {t("dashboard.entities.emailDomain.unavailable")}
          </p>
        )}

        {isEnterprise && featureAvailable && !hasDomain && !isLoading && (
          <div>
            <p className="mb-2 text-sm text-gray-500 font-figtree">
              {t("dashboard.entities.emailDomain.intro")}
            </p>
            {canDeriveDomain ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 flex items-baseline gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 min-w-0">
                  <span className="text-xs text-gray-500 shrink-0">
                    {t("dashboard.entities.emailDomain.domainToAuthenticate")}
                  </span>
                  <code className="font-mono text-sm font-medium text-gray-700 truncate">
                    {derivedDomain}
                  </code>
                </div>
                <CustomButton
                  type="button"
                  title={
                    t("dashboard.entities.emailDomain.registerButton") as string
                  }
                  isLoading={isCreating}
                  disabled={!canWriteEntitySettings || isCreating}
                  onClick={handleCreate}
                />
              </div>
            ) : (
              <p className="text-sm text-amber-700 font-figtree">
                {t("dashboard.entities.emailDomain.needSenderFirst")}
              </p>
            )}
          </div>
        )}

        {isEnterprise && featureAvailable && hasDomain && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <span className="font-mono text-sm font-medium text-gray-700 truncate">
                {domainStatus?.domain}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleVerify}
                  disabled={!canWriteEntitySettings || isVerifying}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2 disabled:opacity-50"
                >
                  {isVerifying
                    ? (t("dashboard.entities.emailDomain.checking") as string)
                    : (t(
                        "dashboard.entities.emailDomain.checkButton"
                      ) as string)}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={!canWriteEntitySettings || isDeleting}
                  className="text-xs font-medium text-red-600 hover:text-red-700 underline underline-offset-2 disabled:opacity-50"
                >
                  {t("dashboard.entities.emailDomain.removeButton")}
                </button>
              </div>
            </div>

            {domainStatus?.status === "verified" ? (
              <p className="text-sm text-green-700 font-figtree">
                {t("dashboard.entities.emailDomain.verifiedHint", {
                  domain: domainStatus?.domain,
                })}
              </p>
            ) : (
              <p className="mb-2 text-sm text-gray-500 font-figtree">
                {t("dashboard.entities.emailDomain.pendingHint")}
              </p>
            )}

            {(domainStatus?.last_error_code || domainStatus?.last_error) && (
              <p className="mb-2 text-sm text-red-700 font-figtree">
                {domainStatus?.last_error_code
                  ? (t(
                      `dashboard.entities.emailDomain.errors.${domainStatus.last_error_code}`,
                      { defaultValue: domainStatus?.last_error || "" }
                    ) as string)
                  : domainStatus?.last_error}
              </p>
            )}

            {(domainStatus?.dns_records?.length ?? 0) > 0 && (
              <div className="space-y-3">
                {domainStatus?.dns_records?.map(recordItem)}
              </div>
            )}

            {senderDomainOf(senderEmail) !==
              (domainStatus?.domain || "").toLowerCase() && (
              <p className="mt-2 text-xs text-amber-700 font-figtree">
                {t("dashboard.entities.emailDomain.senderMismatch", {
                  domain: domainStatus?.domain,
                })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailDomainCard;
